/**
 * Visit Prep Card
 * Shows pre-visit context, key issues, and AI suggestions.
 */
import { useEffect, useState } from 'react';

interface VisitPrepData {
  patient_summary: string;
  key_issues: string[];
  outstanding_items: string[];
  medications_to_review: string[];
  recent_encounters: Array<{ date: string; type: string; procedure: string | null }>;
  suggested_topics: string[];
}

export default function VisitPrepCard({ patientId }: { patientId: string }) {
  const [data, setData] = useState<VisitPrepData | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!patientId) return;
    fetch(`/api/v1/patients/${patientId}/visit-prep`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); if (d) setOpen(true); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [patientId]);

  if (loading) {
    return (
      <div className="bg-gray-100 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">📋 Visit Prep</h3>
        <p className="text-sm text-gray-500 mt-2 flex items-center gap-2">
          <span className="animate-spin">⏳</span> Preparing visit context...
        </p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="bg-gray-100 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
      <button
        onClick={() => setOpen(!open)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-t-xl"
      >
        <div className="text-left">
          <h3 className="text-lg font-semibold flex items-center gap-2">📋 Visit Prep</h3>
          <p className="text-sm text-gray-500">AI-prepared context for today's visit</p>
        </div>
        <span className="text-gray-400">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4">
          {data.key_issues?.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm flex items-center gap-2 mb-2">⚠️ Key Active Issues</h4>
              <div className="space-y-1">
                {data.key_issues.map((issue, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-xs bg-gray-200 dark:bg-gray-600 rounded px-1.5 py-0.5 mt-0.5">{i + 1}</span>
                    <p className="text-sm">{issue}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.outstanding_items?.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm flex items-center gap-2 mb-2">📅 Outstanding Follow-ups</h4>
              <ul className="space-y-1">
                {data.outstanding_items.map((item, i) => (
                  <li key={i} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.medications_to_review?.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm flex items-center gap-2 mb-2">💊 Medications to Review</h4>
              <div className="flex flex-wrap gap-2">
                {data.medications_to_review.map((med, i) => (
                  <span key={i} className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">{med}</span>
                ))}
              </div>
            </div>
          )}

          {data.suggested_topics?.length > 0 && (
            <div className="p-3 bg-purple-50 dark:bg-purple-950 rounded-lg border border-purple-200 dark:border-purple-800">
              <h4 className="font-semibold text-sm text-purple-700 dark:text-purple-300 flex items-center gap-2 mb-2">
                💡 AI Suggested Topics
              </h4>
              <ul className="space-y-1">
                {data.suggested_topics.map((topic, i) => (
                  <li key={i} className="text-sm text-purple-600 dark:text-purple-400 flex items-start gap-2">
                    <span className="mt-1">→</span>
                    <span>{topic}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {data.recent_encounters?.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-2">Recent Encounters</h4>
              <div className="space-y-1">
                {data.recent_encounters.map((enc, i) => (
                  <div key={i} className="text-xs text-gray-500 flex items-center gap-2">
                    <span className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                      {new Date(enc.date).toLocaleDateString()}
                    </span>
                    <span className="capitalize">{enc.type.replace('_', ' ')}</span>
                    {enc.procedure && <span>- {enc.procedure}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
